import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ImagePicker from '@/components/ImagePicker';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Clock, CheckCircle, XCircle, Calendar, Eye, MousePointer } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const TEMPLATES = [
  {
    value: 'classic',
    label: 'Classic',
    desc: '圖片 + 文字 + 按鈕',
    preview: (color) => (
      <div className="bg-white rounded-lg overflow-hidden border text-xs">
        <div className="h-8 bg-gray-200 flex items-center justify-center text-gray-400">圖片</div>
        <div className="p-2 space-y-1">
          <div className="h-2 rounded" style={{ background: color, width: '70%' }} />
          <div className="h-1.5 bg-gray-200 rounded w-full" />
          <div className="h-1.5 bg-gray-200 rounded w-4/5" />
          <div className="h-4 rounded mt-1" style={{ background: color }} />
        </div>
      </div>
    ),
  },
  {
    value: 'offer',
    label: 'Offer',
    desc: '撞色大字優惠',
    preview: (color) => (
      <div className="bg-white rounded-lg overflow-hidden border text-xs">
        <div className="p-3 text-center text-white" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
          <div className="h-2 rounded bg-white/60 w-3/4 mx-auto mb-1" />
          <div className="h-3 rounded bg-white w-full mx-auto" />
        </div>
        <div className="p-2 space-y-1">
          <div className="h-1.5 bg-gray-200 rounded" />
          <div className="h-4 rounded" style={{ background: color }} />
        </div>
      </div>
    ),
  },
  {
    value: 'notice',
    label: 'Notice',
    desc: '左側色條，正式公告',
    preview: (color) => (
      <div className="bg-white rounded-lg overflow-hidden border text-xs">
        <div className="h-1" style={{ background: color }} />
        <div className="p-2 flex gap-2">
          <div className="w-1 rounded-full flex-shrink-0" style={{ background: color, minHeight: '40px' }} />
          <div className="space-y-1 flex-1">
            <div className="h-2 rounded" style={{ background: color, width: '60%' }} />
            <div className="h-1.5 bg-gray-200 rounded" />
            <div className="h-1.5 bg-gray-200 rounded w-4/5" />
            <div className="h-3.5 rounded mt-1" style={{ background: color }} />
          </div>
        </div>
      </div>
    ),
  },
  {
    value: 'whatsapp',
    label: 'WhatsApp',
    desc: '綠色主題，聯絡 CTA',
    preview: () => (
      <div className="bg-white rounded-lg overflow-hidden border text-xs">
        <div className="p-2 text-center bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="text-base mb-0.5">💬</div>
          <div className="h-2 rounded bg-white/70 w-3/4 mx-auto" />
        </div>
        <div className="p-2 space-y-1">
          <div className="h-1.5 bg-gray-200 rounded" />
          <div className="h-4 rounded bg-green-500" />
        </div>
      </div>
    ),
  },
  {
    value: 'banner',
    label: 'Banner',
    desc: '全圖 + 文字疊加',
    preview: (color) => (
      <div className="bg-white rounded-lg overflow-hidden border text-xs">
        <div className="relative h-16 bg-gray-300">
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-1.5">
            <div className="h-2 rounded bg-white w-3/4 mb-0.5" />
            <div className="h-1.5 rounded bg-white/70 w-1/2 mb-1" />
            <div className="h-3 rounded self-start px-2 text-white text-xs flex items-center" style={{ background: color, minWidth: '40px' }} />
          </div>
        </div>
      </div>
    ),
  },
];

const PAGE_OPTIONS = [
  { value: '/', label: '首頁 (/)' },
  { value: '/courses', label: '課程資訊 (/courses)' },
  { value: '/instructors', label: '導師介紹 (/instructors)' },
  { value: '/about', label: '關於我們 (/about)' },
  { value: '/contact', label: '聯絡我們 (/contact)' },
  { value: '/testimonials', label: '學員分享 (/testimonials)' },
  { value: '/blog', label: '考試資料 (/blog)' },
  { value: '/exam-guide', label: '考試攻略 (/exam-guide)' },
];

const TRIGGER_OPTIONS = [
  { value: 'delay', label: '⏱ 停留 X 秒後彈出' },
  { value: 'scroll', label: '📜 捲動 X% 後彈出' },
  { value: 'exit', label: '🚪 Exit Intent（滑鼠移向關閉）' },
];

const FREQ_OPTIONS = [
  { value: 0, label: '每次訪問都顯示' },
  { value: 1, label: '每天最多一次' },
  { value: 3, label: '每 3 天一次' },
  { value: 7, label: '每週一次' },
  { value: 30, label: '每月一次' },
  { value: -1, label: '關閉後永不再顯示' },
];

const ASPECT_RATIOS = [
  { value: '1:1',    label: '1:1　正方形（IG 帖子）' },
  { value: '4:5',    label: '4:5　直向（IG 直向）' },
  { value: '1.91:1', label: '1.91:1  橫向（FB 帖子）' },
  { value: '16:9',   label: '16:9　橫向（Banner）' },
];

const emptyForm = {
  title: '', content: '', image_url: '', image_aspect_ratio: '1:1',
  button_text: '', button_url: '',
  enabled: true, start_at: '', end_at: '',
  trigger_type: 'delay', delay_seconds: 2, scroll_percentage: 50,
  frequency_days: 1, target_pages: [],
  template: 'classic', accent_color: '#2563eb', popup_size: 'lg',
};

const getStatus = (popup) => {
  const now = new Date().toISOString();
  if (!popup.enabled) return { label: '已停用', color: 'bg-gray-100 text-gray-600', icon: XCircle };
  if (popup.start_at && now < popup.start_at) return { label: '排程中', color: 'bg-blue-100 text-blue-700', icon: Calendar };
  if (popup.end_at && now > popup.end_at) return { label: '已過期', color: 'bg-red-100 text-red-600', icon: Clock };
  return { label: '顯示中', color: 'bg-green-100 text-green-700', icon: CheckCircle };
};

const ctr = (p) => p.impressions > 0 ? ((p.clicks / p.impressions) * 100).toFixed(1) + '%' : '–';

const AdminPopups = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPopups(); }, []);

  const fetchPopups = async () => {
    try {
      const res = await axios.get(`${API_URL}/popups`);
      setPopups(res.data);
    } catch { toast.error('載入失敗'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditingId(null); setFormData(emptyForm); setDialogOpen(true); };

  const openEdit = (p) => {
    setEditingId(p.id);
    setFormData({
      title: p.title || '', content: p.content || '',
      image_url: p.image_url || '', image_aspect_ratio: p.image_aspect_ratio || '1:1',
      button_text: p.button_text || '',
      button_url: p.button_url || '', enabled: p.enabled ?? true,
      start_at: p.start_at ? p.start_at.slice(0, 16) : '',
      end_at: p.end_at ? p.end_at.slice(0, 16) : '',
      trigger_type: p.trigger_type || 'delay',
      delay_seconds: p.delay_seconds ?? 2,
      scroll_percentage: p.scroll_percentage ?? 50,
      frequency_days: p.frequency_days ?? 1,
      target_pages: p.target_pages || [],
      template: p.template || 'classic',
      accent_color: p.accent_color || '#2563eb',
      popup_size: p.popup_size || 'lg',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_at: formData.start_at ? new Date(formData.start_at).toISOString() : null,
        end_at: formData.end_at ? new Date(formData.end_at).toISOString() : null,
        delay_seconds: Number(formData.delay_seconds),
        scroll_percentage: Number(formData.scroll_percentage),
        frequency_days: Number(formData.frequency_days),
        popup_size: formData.popup_size || 'lg',
      };
      if (editingId) {
        await axios.put(`${API_URL}/popups/${editingId}`, payload);
        toast.success('已更新');
      } else {
        await axios.post(`${API_URL}/popups`, payload);
        toast.success('已新增');
      }
      setDialogOpen(false);
      fetchPopups();
    } catch { toast.error('儲存失敗'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await axios.delete(`${API_URL}/popups/${deleteTarget}`); toast.success('已刪除'); fetchPopups(); }
    catch { toast.error('刪除失敗'); }
    finally { setDeleteTarget(null); }
  };

  const toggleEnabled = async (p) => {
    try { await axios.put(`${API_URL}/popups/${p.id}`, { enabled: !p.enabled }); fetchPopups(); }
    catch { toast.error('更新失敗'); }
  };

  const togglePage = (page) => {
    setFormData(prev => ({
      ...prev,
      target_pages: prev.target_pages.includes(page)
        ? prev.target_pages.filter(p => p !== page)
        : [...prev.target_pages, page],
    }));
  };

  const set = (k) => (v) => setFormData(p => ({ ...p, [k]: v }));

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">彈出公告管理</h1>
            <p className="text-muted-foreground">管理促銷及公告，支援排程、觸發條件、頁面篩選</p>
          </div>
          <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />新增公告</Button>
        </div>

        {loading ? <div className="text-center py-12">載入中...</div> : popups.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground">尚未有公告</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {popups.map((popup) => {
              const status = getStatus(popup);
              const StatusIcon = status.icon;
              const triggerLabel = TRIGGER_OPTIONS.find(t => t.value === popup.trigger_type)?.label || '';
              const freqLabel = FREQ_OPTIONS.find(f => f.value === popup.frequency_days)?.label || '';
              return (
                <Card key={popup.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {popup.image_url && (
                        <img src={popup.image_url} alt="" className="w-16 h-16 object-contain rounded-lg border flex-shrink-0 bg-gray-50" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold">{popup.title || '（無標題）'}</h3>
                          {popup.template && popup.template !== 'classic' && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{popup.template}</span>
                          )}
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                            <StatusIcon className="h-3 w-3" />{status.label}
                          </span>
                        </div>
                        {popup.content && <p className="text-sm text-muted-foreground line-clamp-1 mb-2">{popup.content}</p>}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>{triggerLabel}</span>
                          <span>{freqLabel}</span>
                          {popup.target_pages?.length > 0
                            ? <span>頁面：{popup.target_pages.join(', ')}</span>
                            : <span>所有頁面</span>}
                          {popup.start_at && <span>開始：{new Date(popup.start_at).toLocaleString('zh-HK')}</span>}
                          {popup.end_at && <span>結束：{new Date(popup.end_at).toLocaleString('zh-HK')}</span>}
                        </div>
                        {/* Stats */}
                        <div className="flex gap-4 mt-2">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Eye className="h-3 w-3" />{popup.impressions || 0} 次顯示
                          </span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MousePointer className="h-3 w-3" />{popup.clicks || 0} 次點擊
                          </span>
                          <span className="text-xs text-muted-foreground">CTR: {ctr(popup)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0 items-start">
                        <Button size="sm" variant="outline" onClick={() => toggleEnabled(popup)}
                          className={popup.enabled ? 'text-green-600 border-green-200' : 'text-gray-400'}>
                          {popup.enabled ? '開啟' : '關閉'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(popup)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(popup.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? '編輯公告' : '新增公告'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">

            {/* Enable */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input type="checkbox" id="enabled" checked={formData.enabled}
                onChange={(e) => set('enabled')(e.target.checked)} className="h-4 w-4" />
              <label htmlFor="enabled" className="font-medium cursor-pointer">啟用此公告</label>
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${formData.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                {formData.enabled ? '開啟' : '關閉'}
              </span>
            </div>

            {/* Template picker */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-medium">選擇款式</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 border rounded-lg overflow-hidden">
                    {[{v:'sm',l:'細 460'},{v:'md',l:'中 620'},{v:'lg',l:'大 820'},{v:'xl',l:'特大 1020'}].map(s => (
                      <button key={s.v} type="button"
                        onClick={() => set('popup_size')(s.v)}
                        className={`px-2.5 py-1 text-xs font-medium transition-colors ${formData.popup_size === s.v ? 'bg-primary text-white' : 'hover:bg-gray-100'}`}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs">顏色</Label>
                    <input type="color" value={formData.accent_color}
                      onChange={(e) => set('accent_color')(e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {TEMPLATES.map(t => (
                  <div key={t.value} onClick={() => set('template')(t.value)}
                    className={`cursor-pointer rounded-lg p-1.5 transition-all ${formData.template === t.value ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-gray-50 border'}`}>
                    {t.preview(formData.accent_color)}
                    <p className="text-xs font-medium text-center mt-1">{t.label}</p>
                    <p className="text-xs text-muted-foreground text-center leading-tight">{t.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Content */}
            <ImagePicker label="公告圖片（可選）" value={formData.image_url} onChange={set('image_url')} />
            <div>
              <Label>標題</Label>
              <Input value={formData.title} onChange={(e) => set('title')(e.target.value)} placeholder="🎉 限時優惠！" className="mt-1" />
            </div>
            <div>
              <Label>內容文字</Label>
              <Textarea value={formData.content} onChange={(e) => set('content')(e.target.value)} rows={3} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>按鈕文字</Label>
                <Input value={formData.button_text} onChange={(e) => set('button_text')(e.target.value)} placeholder="立即查詢" className="mt-1" />
              </div>
              <div>
                <Label>按鈕連結</Label>
                <Input value={formData.button_url} onChange={(e) => set('button_url')(e.target.value)} placeholder="https://wa.me/..." className="mt-1" />
              </div>
            </div>

            {/* Trigger */}
            <div className="border rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">觸發方式</p>
              <div className="grid grid-cols-1 gap-2">
                {TRIGGER_OPTIONS.map(t => (
                  <label key={t.value} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="trigger" value={t.value} checked={formData.trigger_type === t.value}
                      onChange={() => set('trigger_type')(t.value)} />
                    <span className="text-sm">{t.label}</span>
                  </label>
                ))}
              </div>
              {formData.trigger_type === 'delay' && (
                <div>
                  <Label>延遲秒數：{formData.delay_seconds} 秒</Label>
                  <input type="range" min={0} max={15} value={formData.delay_seconds}
                    onChange={(e) => set('delay_seconds')(Number(e.target.value))} className="w-full mt-1" />
                </div>
              )}
              {formData.trigger_type === 'scroll' && (
                <div>
                  <Label>捲動百分比：{formData.scroll_percentage}%</Label>
                  <input type="range" min={10} max={90} step={5} value={formData.scroll_percentage}
                    onChange={(e) => set('scroll_percentage')(Number(e.target.value))} className="w-full mt-1" />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>10%</span><span>90%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Frequency */}
            <div className="border rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">顯示頻率</p>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.frequency_days} onChange={(e) => set('frequency_days')(Number(e.target.value))}>
                {FREQ_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>

            {/* Page targeting */}
            <div className="border rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">顯示頁面</p>
              <p className="text-xs text-muted-foreground">不選 = 所有頁面都顯示</p>
              <div className="grid grid-cols-2 gap-2">
                {PAGE_OPTIONS.map(p => (
                  <label key={p.value} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={formData.target_pages.includes(p.value)}
                      onChange={() => togglePage(p.value)} className="rounded" />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Schedule */}
            <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
              <p className="text-sm font-medium text-blue-700">排程（留空 = 不限時間）</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>開始時間</Label>
                  <Input type="datetime-local" value={formData.start_at} onChange={(e) => set('start_at')(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>結束時間</Label>
                  <Input type="datetime-local" value={formData.end_at} onChange={(e) => set('end_at')(e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1">
                {saving ? '儲存中...' : editingId ? '儲存更改' : '新增公告'}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此彈窗？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，彈窗將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminPopups;
